import React, { useEffect, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { OutlineButton, PrimaryButton } from '../../_core/_ui/buttons.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { DeliveryOption } from '../../../lib/types/communication.types';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import { isValidEmail, isValidPhone } from '../../../lib/helpers/validation.helpers';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { PreviewRecipientSelect } from '../PreviewPanel/preview-recipient-select.component';
import { IPreviewRecipientOption } from '../PreviewPanel/preview-panel.types';
import { messages as previewMessages } from '../PreviewPanel/preview-panel.messages';
import { ITestSourceContext } from './compose-panel.types';
import { parseRecipients } from './test-send.helpers';
import { loadTestRecipients, saveTestRecipients } from './compose-panel.test-recipients';

interface ITestSendDialogProps {
  open: boolean;
  deliveryOption: DeliveryOption;
  // Per-company+user localStorage key for the last-used test recipients.
  storageKey: string;
  // Scopes + gates the "render variables from" source picker.
  testSource: ITestSourceContext;
  onClose: () => void;
  // Delegates the actual send to the container; resolves with the number of
  // messages the server reported sent, or rejects with a user-facing message.
  onSendTest: (emails: string[], phones: string[], testSourceId?: string) => Promise<number>;
}

export const TestSendDialog: React.FC<ITestSendDialogProps> = ({
  open,
  deliveryOption,
  storageKey,
  testSource,
  onClose,
  onSendTest
}) => {
  const showEmail = deliveryOptionRequiresEmail(deliveryOption);
  const showSMS = deliveryOptionRequiresSMS(deliveryOption);
  // Receiver-scoped tokens (player.*) can only render from an exemplar recipient,
  // so the source picker shows + is required exactly when the content uses them.
  const showSourcePicker = testSource.usesReceiverScopedTokens;

  const [emailsRaw, setEmailsRaw] = useState('');
  const [phonesRaw, setPhonesRaw] = useState('');
  const [source, setSource] = useState<IPreviewRecipientOption | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState<number | undefined>();

  // Seed on the open transition only, so a container re-render (fresh defaultSource
  // identity) can't clobber a source pick the user made in the dialog.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setError(undefined);
      setSentCount(undefined);
      setSending(false);
      const saved = loadTestRecipients(storageKey);
      setEmailsRaw(saved.emails);
      setPhonesRaw(saved.phones);
      setSource(testSource.defaultSource);
    }
    wasOpen.current = open;
  }, [open, storageKey, testSource.defaultSource]);

  const handleSend = async () => {
    setError(undefined);
    const emails = showEmail ? parseRecipients(emailsRaw) : [];
    const phones = showSMS ? parseRecipients(phonesRaw) : [];

    if (!emails.length && !phones.length) {
      setError('Enter at least one test recipient.');
      return;
    }
    const badEmail = emails.find((e) => !isValidEmail(e));
    if (badEmail) {
      setError(`"${badEmail}" is not a valid email address.`);
      return;
    }
    const badPhone = phones.find((p) => !isValidPhone(p));
    if (badPhone) {
      setError(`"${badPhone}" is not a valid phone number.`);
      return;
    }
    if (showSourcePicker && !source) {
      setError('Choose a recipient to render variables from.');
      return;
    }

    setSending(true);
    try {
      const testSourceId = showSourcePicker && source ? source.key : undefined;
      const count = await onSendTest(emails, phones, testSourceId);
      // Persist the raw field text so the next open prefills it — including a
      // channel the current deliveryOption hides (both raw values are kept).
      saveTestRecipients(storageKey, { emails: emailsRaw, phones: phonesRaw });
      setSentCount(count);
    } catch (err) {
      console.error('test send failed:', err);
      // `||` (not `??`) so an empty-string `.message` falls through to the
      // user-facing fallback — see REACT_CONVENTIONS.md.
      const message = err && (err as { message?: string }).message;
      setError(message || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  const isSent = typeof sentCount === 'number';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="test-send-title">
      <DialogTitle id="test-send-title">Send a test</DialogTitle>
      <DialogContent>
        {isSent ? (
          <Box py={1} role="status" aria-live="polite">
            <Typography variant="body2">
              Sent {sentCount} test {pluralize(sentCount as number, 'message')}.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Deliver this message to yourself or a colleague before sending it to the real audience.
            </Typography>
            {showEmail && (
              <TextField
                label="Test emails"
                placeholder="me@example.com, colleague@example.com"
                value={emailsRaw}
                onChange={(e) => setEmailsRaw(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                margin="dense"
                variant="outlined"
                helperText="Separate multiple emails with a comma or new line."
              />
            )}
            {showSMS && (
              <TextField
                label="Test phone numbers"
                placeholder="(204) 555-1234"
                value={phonesRaw}
                onChange={(e) => setPhonesRaw(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                margin="dense"
                variant="outlined"
                helperText="Separate multiple numbers with a comma or new line."
              />
            )}
            {showSourcePicker && (
              <Box mt={1}>
                <PreviewRecipientSelect
                  assessmentId={testSource.assessmentId}
                  criteria={testSource.criteria}
                  contextType={testSource.contextType}
                  contextId={testSource.contextId}
                  value={source}
                  onChange={setSource}
                  label={<IntlMsg {...previewMessages.renderVariablesFrom} />}
                  required
                  skip={!open}
                />
              </Box>
            )}
            {error && (
              <Box mt={1}>
                <Typography variant="caption" color="error">{error}</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isSent ? (
          <PrimaryButton onClick={onClose}>Done</PrimaryButton>
        ) : (
          <>
            <OutlineButton onClick={onClose} disabled={sending}>Cancel</OutlineButton>
            <PrimaryButton onClick={handleSend} disabled={sending}>
              {sending && <CircularProgress size={18} style={{ marginRight: 8, color: 'white' }} />}
              {sending ? 'Sending…' : 'Send test'}
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
